import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { 
  User, FileText, CheckCircle, AlertCircle, ChevronRight, ChevronLeft, 
  LogOut, Home, ClipboardList, Users, Download, Eye, 
  Trash2, Search, X, Plus, Check, Clock, XCircle, Upload, Globe,
  Volume2, VolumeX, Briefcase, Heart, Building, DollarSign, Mail, 
  FileCheck, Wifi, HelpCircle, CreditCard, Pencil, History
} from "lucide-react";
import { translations } from "./translations";
import ttsManager, { getStepReadableText } from "./tts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Services offered by PFC
const PFC_SERVICES = [
  { id: "homelessness", label: "Navigating homelessness issues", icon: Home, category: "housing" },
  { id: "housing_assistance", label: "Housing assistance (applying for housing)", icon: Building, category: "housing" },
  { id: "public_benefits", label: "Public benefits (LINK, TANF, Medicaid, etc.)", icon: DollarSign, category: "benefits" },
  { id: "resource_referrals", label: "Resource referrals (food pantry, ESL, immigration, re-entry, healthcare)", icon: HelpCircle, category: "referrals" },
  { id: "financial_counseling", label: "Financial literacy counseling", icon: DollarSign, category: "counseling" },
  { id: "job_readiness", label: "Job readiness (resumes, job search, applications)", icon: Briefcase, category: "employment" },
  { id: "mail_drop", label: "Mail drop services (use PFC as mailing address)", icon: Mail, category: "services" },
  { id: "notary", label: "Homeless Waiver", icon: FileCheck, category: "services" },
  { id: "pfc_id", label: "Pope Francis Center ID", icon: CreditCard, category: "services" },
  { id: "id_assistance", label: "Birth certificate / State ID assistance", icon: FileText, category: "documents" },
  { id: "internet_access", label: "Internet help & access", icon: Wifi, category: "services" },
  { id: "other", label: "Other", icon: Heart, category: "other" },
];

// Language Context
const LanguageContext = createContext(null);

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem("pfc_language") || "en");
  
  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };
  
  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("pfc_language", lang);
  };
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// TTS Context for Text-to-Speech
const TTSContext = createContext(null);

const useTTS = () => {
  const context = useContext(TTSContext);
  if (!context) {
    return { enabled: false, autoRead: false, speaking: false, speak: () => {}, stop: () => {}, toggle: () => {} };
  }
  return context;
};

const TTSProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(localStorage.getItem("pfc_tts") === "true");
  const [autoRead, setAutoRead] = useState(localStorage.getItem("pfc_tts_auto") === "true");
  const [speaking, setSpeaking] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    ttsManager.init(language);
  }, []);

  useEffect(() => {
    ttsManager.setLanguage(language);
  }, [language]);

  const speak = useCallback((text) => {
    if (!enabled || !text) return;
    setSpeaking(true);
    ttsManager.speak(text, () => setSpeaking(false));
  }, [enabled]);

  const stop = useCallback(() => {
    ttsManager.stop();
    setSpeaking(false);
  }, []);

  const toggleEnabled = useCallback(() => {
    const newValue = !enabled;
    setEnabled(newValue);
    localStorage.setItem("pfc_tts", newValue.toString());
    if (!newValue) {
      stop();
    }
  }, [enabled, stop]);

  const toggleAutoRead = useCallback(() => {
    const newValue = !autoRead;
    setAutoRead(newValue);
    localStorage.setItem("pfc_tts_auto", newValue.toString());
  }, [autoRead]);

  return (
    <TTSContext.Provider value={{ 
      enabled, 
      autoRead, 
      speaking, 
      speak, 
      stop, 
      toggleEnabled, 
      toggleAutoRead,
      isSupported: ttsManager.isSupported()
    }}>
      {children}
    </TTSContext.Provider>
  );
};

// Read Aloud Button Component
const ReadAloudButton = ({ text, size = "default" }) => {
  const { enabled, speaking, speak, stop, isSupported } = useTTS();
  
  if (!isSupported) return null;
  
  const handleClick = () => {
    if (speaking) {
      stop();
    } else if (enabled) {
      speak(text);
    }
  };
  
  const baseClasses = size === "large" 
    ? "h-14 px-6 text-lg gap-3" 
    : "h-10 px-4 gap-2";
  
  return (
    <Button
      type="button"
      variant={speaking ? "default" : "outline"}
      onClick={handleClick}
      disabled={!enabled}
      className={`${baseClasses} rounded-full transition-all ${speaking ? 'bg-primary animate-pulse' : 'border-white/20'}`}
      data-testid="read-aloud-btn"
    >
      {speaking ? (
        <>
          <VolumeX className={size === "large" ? "w-6 h-6" : "w-4 h-4"} />
          {size === "large" && "Stop"}
        </>
      ) : (
        <>
          <Volume2 className={size === "large" ? "w-6 h-6" : "w-4 h-4"} />
          {size === "large" && "Read Aloud"}
        </>
      )}
    </Button>
  );
};

// TTS Settings Panel
const TTSSettings = ({ variant = "default" }) => {
  const { enabled, autoRead, toggleEnabled, toggleAutoRead, isSupported } = useTTS();
  const { t } = useLanguage();
  
  if (!isSupported) return null;
  
  if (variant === "compact") {
    return (
      <Button
        variant={enabled ? "default" : "outline"}
        size="sm"
        onClick={toggleEnabled}
        className={`rounded-full ${enabled ? 'bg-primary' : 'border-white/20'}`}
        data-testid="tts-toggle"
      >
        {enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </Button>
    );
  }
  
  return (
    <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
      <Volume2 className="w-6 h-6 text-primary" />
      <div className="flex-1">
        <p className="font-medium">Audio Assistance</p>
        <p className="text-sm text-muted-foreground">Read form questions aloud</p>
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch 
            checked={enabled} 
            onCheckedChange={toggleEnabled}
            data-testid="tts-enabled-switch"
          />
          <span className="text-sm">{enabled ? "On" : "Off"}</span>
        </div>
        {enabled && (
          <div className="flex items-center gap-2">
            <Switch 
              checked={autoRead} 
              onCheckedChange={toggleAutoRead}
              data-testid="tts-auto-switch"
            />
            <span className="text-sm">Auto</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Language Selector Component
const LanguageSelector = ({ variant = "default" }) => {
  const { language, setLanguage } = useLanguage();
  
  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "pl", name: "Polski", flag: "🇵🇱" },
  ];
  
  const currentLang = languages.find(l => l.code === language);
  
  if (variant === "kiosk") {
    return (
      <div className="flex flex-wrap justify-center gap-3">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            data-testid={`lang-${lang.code}`}
            className={`px-6 py-3 rounded-xl text-lg font-medium transition-all flex items-center gap-2 ${
              language === lang.code
                ? "bg-primary text-white"
                : "bg-white/10 text-white/80 hover:bg-white/20"
            }`}
          >
            <span className="text-xl">{lang.flag}</span>
            {lang.name}
          </button>
        ))}
      </div>
    );
  }
  
  return (
    <Select value={language} onValueChange={setLanguage}>
      <SelectTrigger className="w-[140px] bg-white/5 border-white/10" data-testid="language-selector">
        <Globe className="w-4 h-4 mr-2" />
        <SelectValue>{currentLang?.name}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {languages.map((lang) => (
          <SelectItem key={lang.code} value={lang.code}>
            <span className="flex items-center gap-2">
              <span>{lang.flag}</span>
              {lang.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Logo component
const PFCLogo = ({ className = "h-16" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_intake-tablet/artifacts/jti17wr4_FPC%20Resource%20%2B%20connect%20logo%20transparent.png" 
    alt="PFC Resource+Connect" 
    className={className}
  />
);

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("pfc_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("pfc_token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("pfc_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// ============ LOGIN PAGE ============
const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
      toast.success(t("welcomeBack"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen kiosk-bg flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
      </div>
      
      <Card className="w-full max-w-md kiosk-card border-white/10 relative z-10">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <PFCLogo className="h-20" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">{t("staffLogin")}</CardTitle>
            <CardDescription className="text-muted-foreground">
              {t("signInDesc")}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-base">{t("username")}</Label>
              <Input
                id="username"
                data-testid="login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-14 text-lg bg-white/5 border-white/10 focus:border-primary"
                placeholder={t("enterUsername")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-base">{t("password")}</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 text-lg bg-white/5 border-white/10 focus:border-primary"
                placeholder={t("enterPassword")}
                required
              />
            </div>
            <Button 
              type="submit" 
              data-testid="login-submit"
              className="w-full h-14 text-lg font-medium rounded-full glow"
              disabled={loading}
            >
              {loading ? t("signingIn") : t("signIn")}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              {t("backToKiosk")}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ KIOSK HOME ============
const KioskHome = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { speak, enabled } = useTTS();

  useEffect(() => {
    if (enabled) {
      const welcomeText = `${t("welcome")}. ${t("welcomeMessage")}. ${t("howToProceed")}`;
      speak(welcomeText);
    }
  }, [enabled]);

  return (
    <div className="min-h-screen kiosk-bg flex flex-col items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-2xl text-center space-y-6 relative z-10">
        <div className="flex justify-center mb-6">
          <PFCLogo className="h-24" />
        </div>
        
        {/* Language Selector */}
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-3">{t("selectLanguage")}</p>
          <LanguageSelector variant="kiosk" />
        </div>

        {/* Audio Assistance Toggle */}
        <TTSSettings />
        
        <div className="space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            {t("welcome")}
          </h1>
          <p className="text-xl text-muted-foreground max-w-md mx-auto">
            {t("welcomeMessage")}
          </p>
        </div>

        <div className="kiosk-card rounded-3xl p-8 space-y-6">
          <h2 className="text-2xl font-semibold">{t("howToProceed")}</h2>
          
          <div className="grid gap-4">
            <Button
              data-testid="start-intake-btn"
              onClick={() => navigate("/intake")}
              className="h-20 text-xl font-medium rounded-2xl bg-primary hover:bg-primary/90 glow flex flex-col items-center justify-center gap-1"
            >
              <div className="flex items-center gap-3">
                <ClipboardList className="w-7 h-7" />
                <span>{t("firstVisit") || "First Visit"}</span>
              </div>
              <span className="text-sm font-normal opacity-90">{t("firstVisitSubtitle") || "I'm new to Pope Francis Center"}</span>
            </Button>

            <Button
              data-testid="returning-client-btn"
              onClick={() => navigate("/returning")}
              className="h-20 text-xl font-medium rounded-2xl bg-accent hover:bg-accent/90 glow flex flex-col items-center justify-center gap-1"
            >
              <div className="flex items-center gap-3">
                <User className="w-7 h-7" />
                <span>{t("beenHereBefore") || "I've Been Here Before"}</span>
              </div>
              <span className="text-sm font-normal opacity-90">{t("beenHereBeforeSubtitle") || "Returning client — look me up"}</span>
            </Button>

            <Button
              data-testid="conference-referral-btn"
              variant="outline"
              onClick={() => navigate("/referral")}
              className="h-16 text-lg font-medium rounded-2xl border-accent/40 bg-accent/10 hover:bg-accent/20 text-accent flex items-center justify-center gap-3"
            >
              <Users className="w-6 h-6" />
              {t("conferenceReferral") || "SVdP Conference Referral"}
            </Button>
            
            <Button
              data-testid="staff-login-btn"
              variant="outline"
              onClick={() => navigate("/login")}
              className="h-16 text-lg font-medium rounded-2xl border-white/20 hover:bg-white/5 flex items-center justify-center gap-3"
            >
              <User className="w-6 h-6" />
              {t("staffLogin")}
            </Button>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          {t("needAssistance")}
        </p>
      </div>
    </div>
  );
};

// ============ RETURNING CLIENT LOOKUP ============
const ReturningClientLookup = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [flaggedForCaseManager, setFlaggedForCaseManager] = useState(false);

  const handleLookup = async () => {
    if (!firstName.trim() || !lastName.trim() || !dobMonth || !dobDay || !dobYear) {
      toast.error(t("fillRequiredFields") || "Please fill in all fields");
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const dob = `${dobYear}-${dobMonth}-${dobDay}`;
      const res = await axios.post(`${API}/clients/lookup`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        date_of_birth: dob,
      });

      if (res.data.found) {
        toast.success(t("foundYou") || "We found your record!");
        // Build prefill form data
        const c = res.data.client || {};
        // Convert DOB (MM/DD/YYYY) back to YYYY-MM-DD for IntakeWizard
        let intakeDob = "";
        if (c.date_of_birth) {
          const parts = c.date_of_birth.split("/");
          if (parts.length === 3) {
            intakeDob = `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
          }
        }
        navigate("/intake", {
          state: {
            prefill: {
              first_name: c.first_name || firstName.trim(),
              last_name: c.last_name || lastName.trim(),
              middle_initial: c.middle_initial || "",
              date_of_birth: intakeDob,
              phone: c.phone || "",
              email: c.email || "",
              street_address: c.street_address || "",
              city: c.city || "",
              state: c.state || "IL",
              zip_code: c.zip_code || "",
              emergency_contact: c.emergency_contact || { name: "", phone: "", discretion_required: false },
            },
            returning: true,
            source: res.data.source,
          },
        });
      } else {
        setNotFound(true);
        setAttempts((a) => a + 1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  if (flaggedForCaseManager) {
    return (
      <div className="min-h-screen kiosk-bg flex items-center justify-center p-6">
        <div className="w-full max-w-xl text-center space-y-8">
          <div className="kiosk-card rounded-3xl p-12 space-y-6">
            <div className="w-24 h-24 mx-auto bg-accent/20 rounded-full flex items-center justify-center">
              <Users className="w-14 h-14 text-accent" />
            </div>
            <h1 className="text-3xl font-bold">{t("requestCaseManager")}</h1>
            <p className="text-lg text-muted-foreground">{t("caseManagerFlagged")}</p>
            <Button
              data-testid="return-home-btn"
              onClick={() => navigate("/")}
              className="h-14 px-8 text-lg rounded-full"
            >
              {t("backToHome")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen kiosk-bg flex flex-col">
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        <PFCLogo className="h-12" />
        <div className="flex items-center gap-4">
          <LanguageSelector />
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5 mr-2" />
            {t("cancel")}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-xl">
          <div className="kiosk-card rounded-3xl p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-accent/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-accent" />
              </div>
              <h1 className="text-3xl font-bold">{t("returningLookupTitle")}</h1>
              <p className="text-muted-foreground">{t("returningLookupHelp")}</p>
            </div>

            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-base">{t("firstName")} *</Label>
                  <Input
                    data-testid="returning-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="h-14 text-lg bg-white/5 border-white/10"
                    placeholder={t("firstName")}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base">{t("lastName")} *</Label>
                  <Input
                    data-testid="returning-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="h-14 text-lg bg-white/5 border-white/10"
                    placeholder={t("lastName")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-base">{t("dateOfBirth")} *</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Select value={dobMonth} onValueChange={setDobMonth}>
                    <SelectTrigger className="h-14 text-lg bg-white/5 border-white/10" data-testid="returning-dob-month">
                      <SelectValue placeholder={t("month") || "Month"} />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        {v: "01", l: "January"}, {v: "02", l: "February"}, {v: "03", l: "March"},
                        {v: "04", l: "April"}, {v: "05", l: "May"}, {v: "06", l: "June"},
                        {v: "07", l: "July"}, {v: "08", l: "August"}, {v: "09", l: "September"},
                        {v: "10", l: "October"}, {v: "11", l: "November"}, {v: "12", l: "December"}
                      ].map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={dobDay} onValueChange={setDobDay}>
                    <SelectTrigger className="h-14 text-lg bg-white/5 border-white/10" data-testid="returning-dob-day">
                      <SelectValue placeholder={t("day") || "Day"} />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 31}, (_, i) => String(i + 1).padStart(2, "0")).map(d =>
                        <SelectItem key={d} value={d}>{parseInt(d)}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <Select value={dobYear} onValueChange={setDobYear}>
                    <SelectTrigger className="h-14 text-lg bg-white/5 border-white/10" data-testid="returning-dob-year">
                      <SelectValue placeholder={t("year") || "Year"} />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 100}, (_, i) => String(new Date().getFullYear() - i)).map(y =>
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {notFound && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="font-medium">{t("clientNotFound")}</p>
                      <p className="text-sm text-muted-foreground">{t("clientNotFoundHelp")}</p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                data-testid="lookup-submit-btn"
                onClick={handleLookup}
                disabled={loading}
                className="w-full h-14 text-lg rounded-full glow"
              >
                {loading ? (t("lookingUp") || "Looking you up...") : (t("lookUpMe") || "Look Me Up")}
              </Button>

              {attempts >= 2 && notFound && (
                <div className="grid gap-3 pt-2">
                  <Button
                    data-testid="request-case-manager-btn"
                    variant="outline"
                    onClick={() => setFlaggedForCaseManager(true)}
                    className="h-14 text-base rounded-full border-accent/40 bg-accent/10 hover:bg-accent/20 text-accent"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    {t("requestCaseManager")}
                  </Button>
                  <Button
                    data-testid="continue-as-new-btn"
                    variant="outline"
                    onClick={() => navigate("/intake")}
                    className="h-14 text-base rounded-full border-white/20 hover:bg-white/5"
                  >
                    <ClipboardList className="w-5 h-5 mr-2" />
                    {t("continueAsNew")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ CONFERENCE REFERRAL FORM ============
const SVDP_CONFERENCES = [
  "Our Lady of the Rosary",
  "St. Benedict",
  "St. Elizabeth of the Trinity Parish",
  "St. Alphonsus Queen of Apostles",
  "St. Edward",
  "Mary, Mother of God / Pope Francis Center",
  "St. Monica and St. Rosalie Parish",
  "St. Padre Pio Parish",
  "Brothers & Sisters of Love",
  "Other"
];

const ConferenceReferralForm = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referralId, setReferralId] = useState("");
  const [formData, setFormData] = useState({
    conference_name: "",
    other_organization_name: "",
    submitter_name: "",
    submitter_email: "",
    submitter_phone: "",
    referred_full_name: "",
    referred_phone: "",
    referred_email: "",
    reason_for_referral: "",
    additional_information: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.conference_name || !formData.submitter_name || !formData.referred_full_name || !formData.reason_for_referral) {
      toast.error(t("pleaseFillRequired") || "Please fill in all required fields");
      return;
    }
    
    if (!formData.submitter_email && !formData.submitter_phone) {
      toast.error("Please provide your email or phone number so we can contact you if needed");
      return;
    }
    
    if (formData.conference_name === "Other" && !formData.other_organization_name) {
      toast.error(t("pleaseProvideOrgName") || "Please provide your organization name");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/referrals`, formData);
      setReferralId(response.data.referral_id);
      setSubmitted(true);
      toast.success(t("referralSubmitted") || "Referral submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit referral");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen kiosk-bg flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-lg text-center space-y-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold">{t("thankYou") || "Thank You!"}</h1>
          <p className="text-muted-foreground">
            {t("referralReceived") || "Your referral has been received. Our staff will be notified and will prepare to assist when the client arrives."}
          </p>
          <div className="kiosk-card rounded-2xl p-6">
            <p className="text-sm text-muted-foreground">{t("referralId") || "Referral ID"}</p>
            <p className="text-2xl font-mono font-bold text-primary">{referralId}</p>
          </div>
          <Button
            onClick={() => navigate("/")}
            className="h-14 px-8 text-lg rounded-xl"
          >
            {t("backToHome")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen kiosk-bg flex flex-col">
      {/* Header */}
      <div className="border-b border-white/10 bg-background/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <PFCLogo className="h-10" />
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button variant="ghost" onClick={() => navigate("/")} className="gap-2">
              <X className="w-5 h-5" />
              {t("cancel")}
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 max-w-2xl mx-auto w-full px-6 py-8">
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold">{t("conferenceReferral") || "SVdP Conference Referral"}</h1>
            <p className="text-muted-foreground">
              {t("referralDescription") || "Submit a referral for someone who needs assistance at Pope Francis Center"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Referring Conference */}
            <div className="kiosk-card rounded-2xl p-6 space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                {t("referringConference") || "Referring Conference"}
              </h2>
              
              <div className="space-y-2">
                <Label className="text-base">{t("selectConference") || "Select Conference"} *</Label>
                <Select
                  value={formData.conference_name}
                  onValueChange={(v) => updateField("conference_name", v)}
                >
                  <SelectTrigger className="h-14 text-lg bg-white/5 border-white/10" data-testid="conference-select">
                    <SelectValue placeholder={t("selectConference") || "Select a conference..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {SVDP_CONFERENCES.map(conf => (
                      <SelectItem key={conf} value={conf}>{conf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.conference_name === "Other" && (
                <div className="space-y-2 p-4 bg-white/5 rounded-xl border border-white/10">
                  <Label className="text-base">{t("organizationName") || "Organization Name"} *</Label>
                  <Input
                    data-testid="other-org-name"
                    value={formData.other_organization_name}
                    onChange={(e) => updateField("other_organization_name", e.target.value)}
                    className="h-14 text-lg bg-white/5 border-white/10"
                    placeholder={t("enterOrgName") || "Enter organization name"}
                  />
                </div>
              )}
            </div>

            {/* Your Contact Information (Submitter) */}
            <div className="kiosk-card rounded-2xl p-6 space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Your Contact Information
              </h2>
              <p className="text-sm text-muted-foreground">So we can reach you if we have questions about this referral</p>
              
              <div className="space-y-2">
                <Label className="text-base">Your Name *</Label>
                <Input
                  data-testid="submitter-name"
                  value={formData.submitter_name}
                  onChange={(e) => updateField("submitter_name", e.target.value)}
                  className="h-14 text-lg bg-white/5 border-white/10"
                  placeholder="Enter your name"
                  required
                />
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-base">Your Email</Label>
                  <Input
                    data-testid="submitter-email"
                    type="email"
                    value={formData.submitter_email}
                    onChange={(e) => updateField("submitter_email", e.target.value)}
                    className="h-14 text-lg bg-white/5 border-white/10"
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base">Your Phone</Label>
                  <Input
                    data-testid="submitter-phone"
                    value={formData.submitter_phone}
                    onChange={(e) => updateField("submitter_phone", e.target.value)}
                    className="h-14 text-lg bg-white/5 border-white/10"
                    placeholder="(312) 555-0123"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">* Please provide at least an email or phone number</p>
            </div>

            {/* Person Being Referred */}
            <div className="kiosk-card rounded-2xl p-6 space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                {t("personBeingReferred") || "Person Being Referred"}
              </h2>
              
              <div className="space-y-2">
                <Label className="text-base">{t("fullName") || "Full Name"} *</Label>
                <Input
                  data-testid="referred-name"
                  value={formData.referred_full_name}
                  onChange={(e) => updateField("referred_full_name", e.target.value)}
                  className="h-14 text-lg bg-white/5 border-white/10"
                  placeholder={t("enterFullName") || "Enter full name"}
                  required
                />
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-base">{t("phoneNumber") || "Phone Number"}</Label>
                  <Input
                    data-testid="referred-phone"
                    value={formData.referred_phone}
                    onChange={(e) => updateField("referred_phone", e.target.value)}
                    className="h-14 text-lg bg-white/5 border-white/10"
                    placeholder="(312) 555-0123"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base">{t("emailAddress") || "Email Address"}</Label>
                  <Input
                    data-testid="referred-email"
                    type="email"
                    value={formData.referred_email}
                    onChange={(e) => updateField("referred_email", e.target.value)}
                    className="h-14 text-lg bg-white/5 border-white/10"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Referral Details */}
            <div className="kiosk-card rounded-2xl p-6 space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {t("referralDetails") || "Referral Details"}
              </h2>
              
              <div className="space-y-2">
                <Label className="text-base">{t("reasonForReferral") || "Reason for Referral"} *</Label>
                <Textarea
                  data-testid="referral-reason"
                  value={formData.reason_for_referral}
                  onChange={(e) => updateField("reason_for_referral", e.target.value)}
                  className="min-h-[120px] text-lg bg-white/5 border-white/10 resize-none"
                  placeholder={t("describeReason") || "Describe why this person is being referred..."}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-base">{t("additionalInfo") || "Additional Information"}</Label>
                <Textarea
                  data-testid="additional-info"
                  value={formData.additional_information}
                  onChange={(e) => updateField("additional_information", e.target.value)}
                  className="min-h-[100px] text-lg bg-white/5 border-white/10 resize-none"
                  placeholder={t("anyOtherInfo") || "Any other relevant information..."}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/")}
                className="flex-1 h-14 text-lg rounded-xl"
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 h-14 text-lg rounded-xl bg-primary hover:bg-primary/90"
                data-testid="submit-referral-btn"
              >
                {isSubmitting ? (t("submitting") || "Submitting...") : (t("submitReferral") || "Submit Referral")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============ NEW INTAKE WIZARD (Restructured) ============
const IntakeWizard = () => {
  const { t } = useLanguage();
  const { speak, autoRead, enabled, stop } = useTTS();
  const location = useLocation();
  const prefill = location.state?.prefill || {};
  const isReturning = !!location.state?.returning;
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    // Demographics
    first_name: prefill.first_name || "",
    last_name: prefill.last_name || "",
    middle_initial: prefill.middle_initial || "",
    date_of_birth: prefill.date_of_birth || "",
    ssn: "",
    gender: "",
    phone: prefill.phone || "",
    email: prefill.email || "",
    street_address: prefill.street_address || "",
    city: prefill.city || "",
    state: prefill.state || "IL",
    zip_code: prefill.zip_code || "",
    emergency_contact: prefill.emergency_contact || { name: "", phone: "", discretion_required: false },
    
    // Living Situation & Needs
    living_situation: "",
    living_situation_detail: "",
    household: { size: 1, num_adults: 1, num_children: 0, additional_income: 0 },
    income: {},
    
    // Services Selected
    services_requested: [],
    other_needs: "",
    
    // Consents (dynamic based on services)
    consent: {
      informed_consent: false,
      informed_consent_signature: "",
      informed_consent_date: "",
      mail_drop_consent: false,
      mail_drop_signature: "",
      mail_drop_date: "",
      id_card_consent: false,
      id_card_signature: "",
      id_card_date: "",
      do_not_share: "",
    },
    
    entry_mode: isReturning ? "returning" : "self",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [clientId, setClientId] = useState("");
  const navigate = useNavigate();

  const INTAKE_STEPS = [
    { id: "demographics", title: t("personalInfo"), icon: User },
    { id: "contact", title: t("contactAddress"), icon: Home },
    { id: "living", title: t("livingSituation"), icon: Building },
    { id: "services", title: "Services Needed", icon: Heart },
    { id: "consent", title: t("consent"), icon: CheckCircle },
  ];

  // Check which consents are needed based on services
  const needsMailDropConsent = formData.services_requested.includes("mail_drop");
  const needsIdCardConsent = formData.services_requested.includes("id_assistance");

  useEffect(() => {
    if (autoRead && enabled && INTAKE_STEPS[step]) {
      const stepTexts = {
        demographics: t("ttsDemographics"),
        contact: t("ttsContact"),
        living: t("ttsLiving"),
        services: t("ttsServices"),
        consent: t("ttsConsent")
      };
      speak(stepTexts[INTAKE_STEPS[step].id] || '');
    }
    return () => stop();
  }, [step, autoRead, enabled]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedData = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const toggleService = (serviceId) => {
    setFormData((prev) => {
      const current = prev.services_requested || [];
      const updated = current.includes(serviceId)
        ? current.filter((s) => s !== serviceId)
        : [...current, serviceId];
      return { ...prev, services_requested: updated };
    });
  };

  const nextStep = () => {
    if (step < INTAKE_STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Map form data to API format
      const submitData = {
        ...formData,
        demographics: {
          gender: formData.gender,
          date_of_birth: formData.date_of_birth,
          ssn: formData.ssn,
          ethnicity_hispanic: false,
          race: [],
          relationship_status: "",
        },
        presenting_problem: formData.services_requested.map(s => 
          PFC_SERVICES.find(svc => svc.id === s)?.label || s
        ).join(", ") + (formData.other_needs ? ` | Other: ${formData.other_needs}` : ""),
        referral_source: "",
      };
      
      const response = await axios.post(`${API}/intakes`, submitData);
      setClientId(response.data.client_id);
      setSubmitted(true);
      toast.success(t("submissionSuccess"));
      if (enabled) {
        speak(`${t("thankYou")}. ${t("submissionSuccess")}. ${t("caseManagerContact")}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit form");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen kiosk-bg flex items-center justify-center p-6">
        <div className="w-full max-w-xl text-center space-y-8">
          <div className="kiosk-card rounded-3xl p-12 space-y-8">
            <div className="w-24 h-24 mx-auto bg-accent/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-14 h-14 text-accent" />
            </div>
            <div className="space-y-4">
              <h1 className="text-3xl font-bold">{t("thankYou")}</h1>
              <p className="text-xl text-muted-foreground">
                {t("submissionSuccess")}
              </p>
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-sm text-muted-foreground">{t("yourClientId")}</p>
                <p className="text-2xl font-mono font-bold text-primary" data-testid="client-id">{clientId}</p>
              </div>
              <p className="text-muted-foreground">
                {t("caseManagerContact")}
              </p>
            </div>
            <Button
              data-testid="back-to-home-btn"
              onClick={() => navigate("/")}
              className="h-14 px-8 text-lg font-medium rounded-full"
            >
              {t("backToHome")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen kiosk-bg flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        <PFCLogo className="h-12" />
        <div className="flex items-center gap-4">
          <TTSSettings variant="compact" />
          <LanguageSelector />
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5 mr-2" />
            {t("cancel")}
          </Button>
        </div>
      </div>

      {/* Progress */}
      <div className="p-4 bg-card/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-muted-foreground">
              {t("step")} {step + 1} {t("of")} {INTAKE_STEPS.length}
            </span>
            <div className="flex items-center gap-3">
              <ReadAloudButton text={INTAKE_STEPS[step]?.title || ''} />
              <span className="text-sm font-medium">{INTAKE_STEPS[step].title}</span>
            </div>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / INTAKE_STEPS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          {isReturning && step === 0 && (
            <div className="mb-4 rounded-2xl border border-accent/40 bg-accent/10 p-5 flex items-start gap-3" data-testid="returning-banner">
              <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold text-accent">{t("foundYou") || "Welcome back!"}</p>
                <p className="text-sm text-muted-foreground">{t("welcomeBackReview")}</p>
              </div>
            </div>
          )}
          <div className="kiosk-card rounded-3xl p-6 md:p-8">
            {step === 0 && (
              <DemographicsStep formData={formData} updateFormData={updateFormData} t={t} />
            )}
            {step === 1 && (
              <ContactStep formData={formData} updateFormData={updateFormData} updateNestedData={updateNestedData} t={t} />
            )}
            {step === 2 && (
              <LivingSituationStep formData={formData} updateFormData={updateFormData} updateNestedData={updateNestedData} t={t} />
            )}
            {step === 3 && (
              <ServicesStep 
                formData={formData} 
                toggleService={toggleService} 
                updateFormData={updateFormData}
                t={t} 
              />
            )}
            {step === 4 && (
              <ConsentStep 
                formData={formData} 
                updateNestedData={updateNestedData} 
                needsMailDropConsent={needsMailDropConsent}
                needsIdCardConsent={needsIdCardConsent}
                t={t} 
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="p-4 border-t border-white/10 bg-card/50">
        <div className="max-w-2xl mx-auto flex justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={step === 0}
            className="h-14 px-6 text-lg rounded-full border-white/20"
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            {t("previous")}
          </Button>
          
          {step < INTAKE_STEPS.length - 1 ? (
            <Button
              onClick={nextStep}
              className="h-14 px-8 text-lg rounded-full glow"
              data-testid="next-step-btn"
            >
              {t("next")}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.consent.informed_consent}
              className="h-14 px-8 text-lg rounded-full bg-accent hover:bg-accent/90 glow"
              data-testid="submit-intake-btn"
            >
              {submitting ? t("submitting") : t("submit")}
              <Check className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ FORM STEPS ============

const DemographicsStep = ({ formData, updateFormData, t }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-semibold">{t("personalInfo")}</h2>
    <p className="text-muted-foreground">{t("tellUsAboutYourself")}</p>
    
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2">
        <Label className="text-base">{t("firstName")} *</Label>
        <Input
          data-testid="first-name"
          value={formData.first_name}
          onChange={(e) => updateFormData("first_name", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder={t("firstName")}
          required
        />
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t("lastName")} *</Label>
        <Input
          data-testid="last-name"
          value={formData.last_name}
          onChange={(e) => updateFormData("last_name", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder={t("lastName")}
          required
        />
      </div>
    </div>
    
    <div className="grid gap-4 md:grid-cols-3">
      <div className="space-y-2">
        <Label className="text-base">{t("middleInitial")}</Label>
        <Input
          data-testid="middle-initial"
          value={formData.middle_initial}
          onChange={(e) => updateFormData("middle_initial", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder="M.I."
          maxLength={1}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t("dateOfBirth")}</Label>
        <div className="grid grid-cols-3 gap-2">
          <Select
            value={formData.date_of_birth ? formData.date_of_birth.split("-")[1] : ""}
            onValueChange={(month) => {
              const current = formData.date_of_birth ? formData.date_of_birth.split("-") : ["", "", ""];
              updateFormData("date_of_birth", `${current[0] || new Date().getFullYear()}-${month}-${current[2] || "01"}`);
            }}
          >
            <SelectTrigger className="h-14 text-lg bg-white/5 border-white/10" data-testid="dob-month">
              <SelectValue placeholder={t("month") || "Month"} />
            </SelectTrigger>
            <SelectContent>
              {[
                {v: "01", l: "January"}, {v: "02", l: "February"}, {v: "03", l: "March"},
                {v: "04", l: "April"}, {v: "05", l: "May"}, {v: "06", l: "June"},
                {v: "07", l: "July"}, {v: "08", l: "August"}, {v: "09", l: "September"},
                {v: "10", l: "October"}, {v: "11", l: "November"}, {v: "12", l: "December"}
              ].map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={formData.date_of_birth ? formData.date_of_birth.split("-")[2] : ""}
            onValueChange={(day) => {
              const current = formData.date_of_birth ? formData.date_of_birth.split("-") : ["", "", ""];
              updateFormData("date_of_birth", `${current[0] || new Date().getFullYear()}-${current[1] || "01"}-${day}`);
            }}
          >
            <SelectTrigger className="h-14 text-lg bg-white/5 border-white/10" data-testid="dob-day">
              <SelectValue placeholder={t("day") || "Day"} />
            </SelectTrigger>
            <SelectContent>
              {Array.from({length: 31}, (_, i) => String(i + 1).padStart(2, "0")).map(d => 
                <SelectItem key={d} value={d}>{parseInt(d)}</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Select
            value={formData.date_of_birth ? formData.date_of_birth.split("-")[0] : ""}
            onValueChange={(year) => {
              const current = formData.date_of_birth ? formData.date_of_birth.split("-") : ["", "", ""];
              updateFormData("date_of_birth", `${year}-${current[1] || "01"}-${current[2] || "01"}`);
            }}
          >
            <SelectTrigger className="h-14 text-lg bg-white/5 border-white/10" data-testid="dob-year">
              <SelectValue placeholder={t("year") || "Year"} />
            </SelectTrigger>
            <SelectContent>
              {Array.from({length: 100}, (_, i) => String(new Date().getFullYear() - i)).map(y => 
                <SelectItem key={y} value={y}>{y}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t("ssn")}</Label>
        <Input
          data-testid="ssn"
          value={formData.ssn}
          onChange={(e) => updateFormData("ssn", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder="XXX-XX-XXXX"
        />
      </div>
    </div>
    
    <div className="space-y-3">
      <Label className="text-base">{t("gender")}</Label>
      <RadioGroup
        value={formData.gender}
        onValueChange={(v) => updateFormData("gender", v)}
        className="flex flex-wrap gap-4"
      >
        {[
          { value: "male", label: t("male") },
          { value: "female", label: t("female") },
          { value: "other", label: t("other") },
          { value: "prefer_not_to_say", label: t("preferNotToSay") }
        ].map((g) => (
          <div key={g.value} className="flex items-center space-x-2">
            <RadioGroupItem value={g.value} id={`gender-${g.value}`} />
            <Label htmlFor={`gender-${g.value}`} className="text-base cursor-pointer">{g.label}</Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  </div>
);

const ContactStep = ({ formData, updateFormData, updateNestedData, t }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-semibold">{t("contactAddress")}</h2>
    
    <div className="space-y-2">
      <Label className="text-base">{t("streetAddress")}</Label>
      <Input
        data-testid="street-address"
        value={formData.street_address}
        onChange={(e) => updateFormData("street_address", e.target.value)}
        className="h-14 text-lg bg-white/5 border-white/10"
        placeholder={t("streetAddress")}
      />
    </div>
    
    <div className="grid gap-4 md:grid-cols-3">
      <div className="space-y-2">
        <Label className="text-base">{t("city")}</Label>
        <Input
          data-testid="city"
          value={formData.city}
          onChange={(e) => updateFormData("city", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder={t("city")}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t("state")}</Label>
        <Input
          data-testid="state"
          value={formData.state}
          onChange={(e) => updateFormData("state", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder="IL"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t("zipCode")}</Label>
        <Input
          data-testid="zip-code"
          value={formData.zip_code}
          onChange={(e) => updateFormData("zip_code", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder="60601"
        />
      </div>
    </div>
    
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2">
        <Label className="text-base">{t("phoneNumber")}</Label>
        <Input
          data-testid="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => updateFormData("phone", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder="(312) 555-0123"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t("emailAddress")}</Label>
        <Input
          data-testid="email"
          type="email"
          value={formData.email}
          onChange={(e) => updateFormData("email", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder="email@example.com"
        />
      </div>
    </div>

    <Separator className="bg-white/10" />
    
    <h3 className="text-xl font-semibold">{t("emergencyContact")}</h3>
    <p className="text-muted-foreground text-sm">{t("emergencyContactDesc")}</p>
    
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2">
        <Label className="text-base">{t("contactName")}</Label>
        <Input
          data-testid="emergency-name"
          value={formData.emergency_contact.name}
          onChange={(e) => updateNestedData("emergency_contact", "name", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder={t("fullName")}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-base">{t("contactPhone")}</Label>
        <Input
          data-testid="emergency-phone"
          type="tel"
          value={formData.emergency_contact.phone}
          onChange={(e) => updateNestedData("emergency_contact", "phone", e.target.value)}
          className="h-14 text-lg bg-white/5 border-white/10"
          placeholder="(312) 555-0123"
        />
      </div>
    </div>
    
    <div className="flex items-center space-x-3 p-4 bg-white/5 rounded-xl">
      <Checkbox
        data-testid="discretion-required"
        id="discretion"
        checked={formData.emergency_contact.discretion_required}
        onCheckedChange={(c) => updateNestedData("emergency_contact", "discretion_required", c)}
      />
      <Label htmlFor="discretion" className="text-base cursor-pointer">
        {t("discretionRequired")}
      </Label>
    </div>
  </div>
);

const LivingSituationStep = ({ formData, updateFormData, updateNestedData, t }) => {
  const situations = [
    { value: "on_street", label: t("onStreet") },
    { value: "shelter", label: t("shelter") },
    { value: "transitional", label: t("transitional") },
    { value: "group_home", label: t("groupHome") },
    { value: "drug_treatment", label: t("drugTreatment") },
    { value: "sro", label: t("sro") },
    { value: "rental", label: t("rental") },
    { value: "own_home", label: t("ownHome") },
    { value: "family_friends", label: t("familyFriends") },
    { value: "temporary", label: t("temporary") },
    { value: "other", label: t("other") },
  ];

  const incomeFields = [
    { key: "employment", label: t("employment") },
    { key: "social_security", label: t("socialSecurity") },
    { key: "ssi", label: t("ssi") },
    { key: "ssd", label: t("ssd") },
    { key: "tanf", label: t("tanf") },
    { key: "food_stamps", label: t("foodStamps") },
    { key: "unemployment", label: t("unemployment") },
    { key: "other", label: t("otherIncome") },
  ];

  const total = incomeFields.reduce((sum, f) => sum + (parseFloat(formData.income[f.key]) || 0), 0);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">{t("livingSituation")}</h2>
      <p className="text-muted-foreground">{t("selectLivingSituation")}</p>
      
      <div className="grid gap-3 md:grid-cols-2">
        {situations.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => updateFormData("living_situation", s.value)}
            className={`p-4 text-left rounded-xl border transition-all ${
              formData.living_situation === s.value
                ? "border-primary bg-primary/10 text-foreground"
                : "border-white/10 bg-white/5 text-muted-foreground hover:border-white/20"
            }`}
          >
            <span className="text-lg">{s.label}</span>
          </button>
        ))}
      </div>
      
      {(formData.living_situation === "sro" || formData.living_situation === "other") && (
        <div className="space-y-2">
          <Label className="text-base">{t("pleaseSpecify")}</Label>
          <Input
            data-testid="living-detail"
            value={formData.living_situation_detail}
            onChange={(e) => updateFormData("living_situation_detail", e.target.value)}
            className="h-14 text-lg bg-white/5 border-white/10"
            placeholder={t("pleaseSpecify")}
          />
        </div>
      )}

      <Separator className="bg-white/10" />
      
      <h3 className="text-xl font-semibold">{t("household")}</h3>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label className="text-base">{t("householdSize")}</Label>
          <Input
            data-testid="household-size"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formData.household.size === 0 ? "" : formData.household.size}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d+$/.test(val)) {
                updateNestedData("household", "size", val === "" ? 0 : parseInt(val));
              }
            }}
            onBlur={(e) => {
              if (!e.target.value || parseInt(e.target.value) < 1) {
                updateNestedData("household", "size", 1);
              }
            }}
            className="h-14 text-lg bg-white/5 border-white/10"
            placeholder="1"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-base">{t("numberOfAdults")}</Label>
          <Input
            data-testid="num-adults"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formData.household.num_adults === 0 ? "" : formData.household.num_adults}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d+$/.test(val)) {
                updateNestedData("household", "num_adults", val === "" ? 0 : parseInt(val));
              }
            }}
            onBlur={(e) => {
              if (!e.target.value || parseInt(e.target.value) < 1) {
                updateNestedData("household", "num_adults", 1);
              }
            }}
            className="h-14 text-lg bg-white/5 border-white/10"
            placeholder="1"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-base">{t("numberOfChildren")}</Label>
          <Input
            data-testid="num-children"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formData.household.num_children === 0 ? "" : formData.household.num_children}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d+$/.test(val)) {
                updateNestedData("household", "num_children", val === "" ? 0 : parseInt(val));
              }
            }}
            className="h-14 text-lg bg-white/5 border-white/10"
            placeholder="0"
          />
        </div>
      </div>

      <Separator className="bg-white/10" />

      <h3 className="text-xl font-semibold">{t("monthlyIncomeTitle")}</h3>
      <p className="text-muted-foreground text-sm">{t("incomeDescription")}</p>
      
      <div className="grid gap-4 md:grid-cols-2">
        {incomeFields.map((field) => (
          <div key={field.key} className="space-y-2">
            <Label className="text-base">{field.label}</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.income[field.key] || ""}
                onChange={(e) => updateNestedData("income", field.key, parseFloat(e.target.value) || 0)}
                className="h-14 text-lg bg-white/5 border-white/10 pl-8"
                placeholder="0.00"
              />
            </div>
          </div>
        ))}
      </div>
      
      <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
        <div className="flex justify-between items-center">
          <span className="text-lg font-medium">{t("totalMonthlyIncome")}:</span>
          <span className="text-2xl font-bold text-primary">${total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

const ServicesStep = ({ formData, toggleService, updateFormData, t }) => {
  // Get translated service labels
  const getServiceLabel = (serviceId) => {
    const translationKeys = {
      homelessness: "serviceHomelessness",
      housing_assistance: "serviceHousing",
      public_benefits: "serviceBenefits",
      resource_referrals: "serviceReferrals",
      financial_counseling: "serviceFinancial",
      job_readiness: "serviceJob",
      mail_drop: "serviceMailDrop",
      notary: "serviceNotary",
      pfc_id: "servicePfcId",
      id_assistance: "serviceId",
      internet_access: "serviceInternet",
      other: "other"
    };
    return t(translationKeys[serviceId]) || PFC_SERVICES.find(s => s.id === serviceId)?.label || serviceId;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">{t("whatBroughtYou")}</h2>
      <p className="text-muted-foreground">
        {t("selectServices")}
      </p>
      
      <div className="space-y-3">
        {PFC_SERVICES.map((service) => {
          const Icon = service.icon;
          const isSelected = formData.services_requested.includes(service.id);
          
          return (
            <button
              key={service.id}
              type="button"
              onClick={() => toggleService(service.id)}
              data-testid={`service-${service.id}`}
              className={`w-full p-4 text-left rounded-xl border transition-all flex items-center gap-4 ${
                isSelected
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-white/10 bg-white/5 text-muted-foreground hover:border-white/20 hover:bg-white/10"
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isSelected ? "bg-primary/20" : "bg-white/10"
              }`}>
                <Icon className={`w-6 h-6 ${isSelected ? "text-primary" : ""}`} />
              </div>
              <span className="text-lg flex-1">{getServiceLabel(service.id)}</span>
              {isSelected && <Check className="w-6 h-6 text-primary" />}
            </button>
          );
        })}
      </div>
      
      {formData.services_requested.includes("other") && (
        <div className="space-y-2">
          <Label className="text-base">{t("tellUsWhatYouNeed")}</Label>
          <Textarea
            data-testid="other-needs"
            value={formData.other_needs}
            onChange={(e) => updateFormData("other_needs", e.target.value)}
            className="min-h-[120px] text-lg bg-white/5 border-white/10 resize-none"
            placeholder={t("describeNeeds")}
          />
        </div>
      )}
      
      {formData.services_requested.length > 0 && (
        <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
          <p className="text-sm text-muted-foreground mb-2">{t("servicesSelected")}:</p>
          <div className="flex flex-wrap gap-2">
            {formData.services_requested.map((id) => (
              <Badge key={id} className="bg-accent/20 text-accent border-accent/30">
                {getServiceLabel(id)}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Signature Canvas Component
const SignatureCanvas = ({ value, onChange, label }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const { t } = useLanguage();

  const startDrawing = useCallback((e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#fff";
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing]);

  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      onChange(canvas.toDataURL());
    }
  }, [isDrawing, onChange]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label className="text-base">{label}</Label>
        <Button type="button" variant="ghost" size="sm" onClick={clearCanvas}>
          {t("clear")}
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="signature-canvas w-full"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
    </div>
  );
};

const ConsentStep = ({ formData, updateNestedData, needsMailDropConsent, needsIdCardConsent, t }) => {
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">{t("consent")}</h2>
      <p className="text-muted-foreground">
        {t("consentIntro")}
      </p>
      
      {/* Informed Consent - Always Required */}
      <div className="p-6 bg-white/5 rounded-xl space-y-4">
        <h3 className="text-xl font-medium">{t("informedConsent")} <span className="text-destructive">*</span></h3>
        <ScrollArea className="h-40 rounded border border-white/10 p-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("informedConsentText")}
          </p>
        </ScrollArea>
        <div className="flex items-center space-x-3">
          <Checkbox
            data-testid="informed-consent"
            id="informed-consent"
            checked={formData.consent.informed_consent}
            onCheckedChange={(c) => {
              updateNestedData("consent", "informed_consent", c);
              if (c) updateNestedData("consent", "informed_consent_date", today);
            }}
          />
          <Label htmlFor="informed-consent" className="text-base cursor-pointer">
            {t("iAgree")} *
          </Label>
        </div>
        {formData.consent.informed_consent && (
          <SignatureCanvas
            value={formData.consent.informed_consent_signature}
            onChange={(sig) => updateNestedData("consent", "informed_consent_signature", sig)}
            label={t("signature")}
          />
        )}
      </div>

      {/* Mail Drop Consent - Only if mail_drop service selected */}
      {needsMailDropConsent && (
        <div className="p-6 bg-blue-500/10 rounded-xl space-y-4 border border-blue-500/20">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            <h3 className="text-xl font-medium">{t("mailDropConsent")}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("mailDropText")}
          </p>
          <div className="flex items-center space-x-3">
            <Checkbox
              data-testid="mail-drop-consent"
              id="mail-drop-consent"
              checked={formData.consent.mail_drop_consent}
              onCheckedChange={(c) => {
                updateNestedData("consent", "mail_drop_consent", c);
                if (c) updateNestedData("consent", "mail_drop_date", today);
              }}
            />
            <Label htmlFor="mail-drop-consent" className="text-base cursor-pointer">
              {t("useMailDrop")}
            </Label>
          </div>
        </div>
      )}

      {/* ID Card Consent - Only if id_assistance service selected */}
      {needsIdCardConsent && (
        <div className="p-6 bg-emerald-500/10 rounded-xl space-y-4 border border-emerald-500/20">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            <h3 className="text-xl font-medium">{t("idCardConsent")}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("idCardText")}
          </p>
          <div className="flex items-center space-x-3">
            <Checkbox
              data-testid="id-card-consent"
              id="id-card-consent"
              checked={formData.consent.id_card_consent}
              onCheckedChange={(c) => {
                updateNestedData("consent", "id_card_consent", c);
                if (c) updateNestedData("consent", "id_card_date", today);
              }}
            />
            <Label htmlFor="id-card-consent" className="text-base cursor-pointer">
              {t("receiveIdCard")}
            </Label>
          </div>
        </div>
      )}

      {/* Do Not Share */}
      <div className="space-y-2">
        <Label className="text-base">{t("doNotShare")}</Label>
        <Textarea
          data-testid="do-not-share"
          value={formData.consent.do_not_share}
          onChange={(e) => updateNestedData("consent", "do_not_share", e.target.value)}
          className="min-h-[100px] text-lg bg-white/5 border-white/10 resize-none"
          placeholder={t("doNotSharePlaceholder")}
        />
      </div>
    </div>
  );
};

// ============ DASHBOARD ============
const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("intakes"); // "intakes" or "referrals"
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [filter, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, intakesRes, referralsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/intakes${filter !== "all" ? `?status=${filter}` : ""}`),
        axios.get(`${API}/referrals`),
      ]);
      setStats(statsRes.data);
      setIntakes(intakesRes.data);
      setReferrals(referralsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  const filteredIntakes = intakes.filter((i) => {
    const search = searchTerm.toLowerCase();
    return (
      i.first_name?.toLowerCase().includes(search) ||
      i.last_name?.toLowerCase().includes(search) ||
      i.client_id?.toLowerCase().includes(search)
    );
  });

  const handleReview = async (intakeId, status, notes = "") => {
    try {
      await axios.post(`${API}/intakes/${intakeId}/review`, {
        status,
        review_notes: notes,
      });
      toast.success(`Intake ${status}`);
      fetchData();
      setShowDetail(false);
    } catch (error) {
      toast.error("Failed to update intake");
    }
  };

  const handleExport = async (intakeId) => {
    try {
      const res = await axios.get(`${API}/intakes/${intakeId}/export`);
      const csvContent = Object.entries(res.data.csv_data)
        .map(([key, val]) => `"${key}","${val}"`)
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename;
      a.click();
      toast.success("Export downloaded");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Export failed");
    }
  };

  const handleConvertReferral = async (referralId) => {
    try {
      const res = await axios.post(`${API}/referrals/${referralId}/convert`);
      toast.success(t("referralConverted") || "Referral converted to intake");
      fetchData();
      // Optionally navigate to the new intake
      navigate(`/dashboard`);
      setActiveTab("intakes");
      setFilter("pending");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert referral");
    }
  };

  const handleCancelReferral = async (referralId) => {
    if (!window.confirm(t("confirmCancelReferral") || "Are you sure you want to cancel this referral?")) {
      return;
    }
    try {
      await axios.post(`${API}/referrals/${referralId}/cancel`);
      toast.success(t("referralCancelled") || "Referral cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel referral");
    }
  };

  const pendingReferrals = referrals.filter(r => r.status === 'pending');

  const getReferralStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="badge-pending"><Clock className="w-3 h-3 mr-1" />{t("pending")}</Badge>,
      converted: <Badge className="badge-approved"><CheckCircle className="w-3 h-3 mr-1" />{t("converted") || "Converted"}</Badge>,
      cancelled: <Badge className="badge-rejected"><XCircle className="w-3 h-3 mr-1" />{t("cancelled") || "Cancelled"}</Badge>,
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="badge-pending"><Clock className="w-3 h-3 mr-1" />{t("pending")}</Badge>,
      approved: <Badge className="badge-approved"><CheckCircle className="w-3 h-3 mr-1" />{t("approved")}</Badge>,
      rejected: <Badge className="badge-rejected"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>,
      exported: <Badge className="badge-exported"><Upload className="w-3 h-3 mr-1" />{t("exported")}</Badge>,
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card/50 hidden md:flex flex-col p-6">
        <div className="mb-8">
          <PFCLogo className="h-12" />
        </div>
        
        <nav className="flex-1 space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-base"
            onClick={() => setFilter("all")}
          >
            <Home className="w-5 h-5 mr-3" />
            {t("dashboard")}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-base"
            onClick={() => setFilter("pending")}
          >
            <Clock className="w-5 h-5 mr-3" />
            {t("pendingReview")}
            {stats?.pending > 0 && (
              <Badge className="ml-auto bg-yellow-500/20 text-yellow-500">{stats.pending}</Badge>
            )}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-base"
            onClick={() => setFilter("approved")}
          >
            <CheckCircle className="w-5 h-5 mr-3" />
            {t("approved")}
          </Button>
          
          <Separator className="my-2 bg-white/10" />
          
          <Button
            variant="ghost"
            className={`w-full justify-start h-12 text-base ${activeTab === 'referrals' ? 'bg-accent/20 text-accent' : ''}`}
            onClick={() => {
              setActiveTab("referrals");
              setFilter("all");
            }}
          >
            <FileText className="w-5 h-5 mr-3" />
            {t("pendingReferrals") || "Referrals"}
            {pendingReferrals.length > 0 && (
              <Badge className="ml-auto bg-accent/20 text-accent">{pendingReferrals.length}</Badge>
            )}
          </Button>
          
          <Separator className="my-2 bg-white/10" />
          
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-base"
            onClick={() => navigate("/intake-entry")}
          >
            <Plus className="w-5 h-5 mr-3" />
            {t("newIntake")}
          </Button>
          {user?.role === "admin" && (
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => {
                fetchUsers();
                setShowUserModal(true);
              }}
            >
              <Users className="w-5 h-5 mr-3" />
              {t("manageUsers")}
            </Button>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-base"
            onClick={() => setShowClientModal(true)}
            data-testid="legacy-clients-btn"
          >
            <Upload className="w-5 h-5 mr-3" />
            Legacy Clients
          </Button>
        </nav>

        <div className="pt-6 border-t border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={logout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            {t("signOut")}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <header className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t("intakeDashboard")}</h1>
            <p className="text-muted-foreground">{t("manageIntakes")}</p>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                data-testid="search-intakes"
                placeholder={t("searchClients")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64 bg-white/5 border-white/10"
              />
            </div>
          </div>
        </header>

        {/* Stats */}
        {stats && (
          <div className="p-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: t("total"), value: stats.total, color: "text-foreground" },
              { label: t("pending"), value: stats.pending, color: "text-yellow-500" },
              { label: t("approved"), value: stats.approved, color: "text-emerald-500" },
              { label: t("exported"), value: stats.exported, color: "text-blue-500" },
              { label: t("today"), value: stats.today, color: "text-primary" },
            ].map((stat) => (
              <Card key={stat.label} className="bg-card/50 border-white/10">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Intakes Table */}
        {activeTab === "intakes" && (
          <div className="p-6">
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle>{t("allIntakes")}</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : filteredIntakes.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t("noIntakesFound")}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("clientId")}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("name")}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">Services</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("status")}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("created")}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredIntakes.map((intake) => (
                          <tr key={intake.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 font-mono text-sm">{intake.client_id}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span>{intake.first_name} {intake.last_name}</span>
                                {intake.visit_history_count > 0 && (
                                  <Badge
                                    variant="outline"
                                    className="border-amber-500/50 bg-amber-500/10 text-amber-400 text-xs"
                                    title={intake.last_prior_visit ? `Last visit: ${new Date(intake.last_prior_visit).toLocaleDateString()}` : ""}
                                    data-testid={`duplicate-badge-${intake.id}`}
                                  >
                                    <History className="w-3 h-3 mr-1" />
                                    {intake.visit_history_count} prior
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-sm text-muted-foreground max-w-[200px] truncate">
                              {intake.presenting_problem || "—"}
                            </td>
                            <td className="p-3">{getStatusBadge(intake.status)}</td>
                            <td className="p-3 text-muted-foreground text-sm">
                              {new Date(intake.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  data-testid={`view-intake-${intake.id}`}
                                  onClick={() => {
                                    setSelectedIntake(intake);
                                    setShowDetail(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {intake.status === "approved" && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`export-intake-${intake.id}`}
                                    onClick={() => handleExport(intake.id)}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Referrals Table */}
        {activeTab === "referrals" && (
          <div className="p-6">
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-accent" />
                  {t("pendingReferrals") || "Conference Referrals"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : referrals.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t("noReferralsFound") || "No referrals found"}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("referralId") || "Referral ID"}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("referringConference") || "Conference"}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">Submitted By</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("personBeingReferred") || "Person Referred"}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("reasonForReferral") || "Reason"}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("status")}</th>
                          <th className="text-left p-3 text-muted-foreground font-medium">{t("actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((referral) => (
                          <tr key={referral.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 font-mono text-sm">{referral.referral_id}</td>
                            <td className="p-3">
                              <p className="font-medium">{referral.conference_name === "Other" ? referral.other_organization_name : referral.conference_name}</p>
                            </td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium">{referral.submitter_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {referral.submitter_email || referral.submitter_phone || "No contact"}
                                </p>
                              </div>
                            </td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium">{referral.referred_full_name}</p>
                                {referral.referred_phone && (
                                  <p className="text-xs text-muted-foreground">{referral.referred_phone}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-sm text-muted-foreground max-w-[200px]">
                              <p className="truncate" title={referral.reason_for_referral}>
                                {referral.reason_for_referral}
                              </p>
                            </td>
                            <td className="p-3">{getReferralStatusBadge(referral.status)}</td>
                            <td className="p-3 text-muted-foreground text-sm">
                              {new Date(referral.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3">
                              {referral.status === "pending" ? (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    data-testid={`convert-referral-${referral.id}`}
                                    onClick={() => handleConvertReferral(referral.id)}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    {t("convertToIntake") || "Convert"}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`cancel-referral-${referral.id}`}
                                    onClick={() => handleCancelReferral(referral.id)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  {referral.status === "converted" && referral.converted_by && (
                                    <span>by {referral.converted_by}</span>
                                  )}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Intake Detail Modal */}
      <IntakeDetailModal
        intake={selectedIntake}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        onReview={handleReview}
        onExport={handleExport}
        t={t}
      />

      {/* User Management Modal */}
      <UserManagementModal
        open={showUserModal}
        onClose={() => setShowUserModal(false)}
        users={users}
        onRefresh={fetchUsers}
        t={t}
      />

      {/* Legacy Clients Modal */}
      <LegacyClientsModal
        open={showClientModal}
        onClose={() => setShowClientModal(false)}
        isAdmin={user?.role === "admin"}
      />
    </div>
  );
};

// Intake Detail Modal
const IntakeDetailModal = ({ intake, open, onClose, onReview, onExport, t }) => {
  const [reviewNotes, setReviewNotes] = useState("");
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (open && intake?.id) {
      axios
        .get(`${API}/intakes/${intake.id}/history`)
        .then((res) => setHistory(res.data))
        .catch(() => setHistory(null));
    } else {
      setHistory(null);
    }
  }, [open, intake?.id]);

  if (!intake) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Client: {intake.client_id}</span>
            {intake.status === "pending" ? (
              <Badge className="badge-pending"><Clock className="w-3 h-3 mr-1" />{t("pending")}</Badge>
            ) : intake.status === "approved" ? (
              <Badge className="badge-approved"><CheckCircle className="w-3 h-3 mr-1" />{t("approved")}</Badge>
            ) : intake.status === "exported" ? (
              <Badge className="badge-exported"><Upload className="w-3 h-3 mr-1" />{t("exported")}</Badge>
            ) : (
              <Badge className="badge-rejected"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Submitted {new Date(intake.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Demographics */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t("personalInfo")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-xs text-muted-foreground uppercase">Name</p><p>{intake.first_name} {intake.last_name}</p></div>
              <div><p className="text-xs text-muted-foreground uppercase">DOB</p><p>{intake.demographics?.date_of_birth || "N/A"}</p></div>
              <div><p className="text-xs text-muted-foreground uppercase">Phone</p><p>{intake.phone || "N/A"}</p></div>
              <div><p className="text-xs text-muted-foreground uppercase">Email</p><p>{intake.email || "N/A"}</p></div>
            </div>
            <Separator className="bg-white/10" />
          </div>

          {/* Living Situation */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t("livingSituation")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-xs text-muted-foreground uppercase">Housing</p><p>{intake.living_situation?.replace("_", " ") || "N/A"}</p></div>
              <div><p className="text-xs text-muted-foreground uppercase">Household Size</p><p>{intake.household?.size || 1}</p></div>
            </div>
            <Separator className="bg-white/10" />
          </div>

          {/* Services Requested */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Services Requested</h3>
            <p className="text-sm">{intake.presenting_problem || "No services specified"}</p>
            <Separator className="bg-white/10" />
          </div>

          {/* Consent Status */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">{t("consent")}</h3>
            <div className="flex gap-4 flex-wrap">
              <Badge variant={intake.consent?.informed_consent ? "default" : "outline"}>
                {intake.consent?.informed_consent ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                {t("informedConsent")}
              </Badge>
              <Badge variant={intake.consent?.mail_drop_consent ? "default" : "outline"}>
                {intake.consent?.mail_drop_consent ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                Mail Drop
              </Badge>
              <Badge variant={intake.consent?.id_card_consent ? "default" : "outline"}>
                {intake.consent?.id_card_consent ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                ID Card
              </Badge>
            </div>
          </div>

          {/* Visit History */}
          {history && history.count > 0 && (
            <div className="space-y-3" data-testid="visit-history-section">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-amber-400" />
                Previous Visits ({history.count})
              </h3>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 divide-y divide-white/5">
                {history.visits.slice(0, 10).map((v) => (
                  <div key={v.id} className="p-3 flex items-start justify-between gap-3" data-testid={`history-visit-${v.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-mono text-muted-foreground">{v.client_id}</span>
                        <Badge variant="outline" className="text-xs capitalize">{v.status}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(v.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {v.services_requested?.length > 0 && (
                        <p className="text-sm mt-1 text-muted-foreground">
                          Services: {v.services_requested.slice(0, 5).join(", ")}
                          {v.services_requested.length > 5 ? ` +${v.services_requested.length - 5} more` : ""}
                        </p>
                      )}
                      {v.presenting_problem && (
                        <p className="text-xs mt-1 text-muted-foreground italic line-clamp-2">
                          {v.presenting_problem}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {history.count > 10 && (
                  <div className="p-2 text-center text-xs text-muted-foreground">
                    …and {history.count - 10} more visit(s)
                  </div>
                )}
              </div>
              <Separator className="bg-white/10" />
            </div>
          )}

          {/* Review Notes */}
          {intake.status === "pending" && (
            <div className="space-y-3">
              <Label>{t("reviewNotes")}</Label>
              <Textarea
                data-testid="review-notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add notes about this intake..."
                className="bg-white/5 border-white/10"
              />
            </div>
          )}

          {intake.reviewed_by && (
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Reviewed by {intake.reviewed_by} on {new Date(intake.reviewed_at).toLocaleString()}
              </p>
              {intake.review_notes && <p className="mt-2">{intake.review_notes}</p>}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {intake.status === "pending" && (
            <>
              <Button
                variant="outline"
                data-testid="reject-intake"
                onClick={() => onReview(intake.id, "rejected", reviewNotes)}
                className="border-destructive text-destructive hover:bg-destructive/10"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {t("reject")}
              </Button>
              <Button
                data-testid="approve-intake"
                onClick={() => onReview(intake.id, "approved", reviewNotes)}
                className="bg-accent hover:bg-accent/90"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {t("approve")}
              </Button>
            </>
          )}
          {intake.status === "approved" && (
            <Button onClick={() => onExport(intake.id)} data-testid="export-intake-detail">
              <Download className="w-4 h-4 mr-2" />
              {t("exportForServWare")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// User Management Modal
const UserManagementModal = ({ open, onClose, users, onRefresh, t }) => {
  const [newUser, setNewUser] = useState({ username: "", password: "", full_name: "", role: "case_manager" });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ full_name: "", role: "", password: "" });

  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success(t("userCreated"));
      setNewUser({ username: "", password: "", full_name: "", role: "case_manager" });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditDraft({ full_name: u.full_name || "", role: u.role || "case_manager", password: "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ full_name: "", role: "", password: "" });
  };

  const saveEdit = async (userId) => {
    const payload = {};
    if (editDraft.full_name) payload.full_name = editDraft.full_name;
    if (editDraft.role) payload.role = editDraft.role;
    if (editDraft.password) payload.password = editDraft.password;
    if (Object.keys(payload).length === 0) {
      toast.error("Nothing to update");
      return;
    }
    try {
      await axios.put(`${API}/users/${userId}`, payload);
      toast.success("User updated");
      cancelEdit();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success(t("userDeleted"));
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-white/10">
        <DialogHeader>
          <DialogTitle>{t("manageUsers")}</DialogTitle>
          <DialogDescription>Add, edit, reset password, or remove staff</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4 p-4 bg-white/5 rounded-lg">
            <h3 className="font-medium">{t("addNewUser")}</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                data-testid="new-user-username"
                placeholder={t("username")}
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="bg-white/5 border-white/10"
              />
              <Input
                data-testid="new-user-password"
                type="password"
                placeholder={t("password")}
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="bg-white/5 border-white/10"
              />
              <Input
                data-testid="new-user-fullname"
                placeholder={t("fullName")}
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                className="bg-white/5 border-white/10"
              />
              <Select
                value={newUser.role}
                onValueChange={(v) => setNewUser({ ...newUser, role: v })}
              >
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("admin")}</SelectItem>
                  <SelectItem value="case_manager">{t("caseManager")}</SelectItem>
                  <SelectItem value="hospitality_manager">{t("hospitalityManager")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleCreate}
              disabled={creating || !newUser.username || !newUser.password || !newUser.full_name}
              data-testid="create-user-btn"
            >
              {creating ? t("creating") : t("createUser")}
            </Button>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">{t("currentUsers")}</h3>
            {users.map((user) => (
              <div key={user.id} className="p-3 bg-white/5 rounded-lg">
                {editingId === user.id ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Full Name</Label>
                        <Input
                          data-testid={`edit-fullname-${user.id}`}
                          value={editDraft.full_name}
                          onChange={(e) => setEditDraft({ ...editDraft, full_name: e.target.value })}
                          className="bg-white/5 border-white/10 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Role</Label>
                        <Select
                          value={editDraft.role}
                          onValueChange={(v) => setEditDraft({ ...editDraft, role: v })}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 h-9" data-testid={`edit-role-${user.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">{t("admin")}</SelectItem>
                            <SelectItem value="case_manager">{t("caseManager")}</SelectItem>
                            <SelectItem value="hospitality_manager">{t("hospitalityManager")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-muted-foreground">New Password (optional — leave blank to keep current)</Label>
                        <Input
                          data-testid={`edit-password-${user.id}`}
                          type="password"
                          placeholder="Leave blank to keep current password"
                          value={editDraft.password}
                          onChange={(e) => setEditDraft({ ...editDraft, password: e.target.value })}
                          className="bg-white/5 border-white/10 h-9"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(user.id)} data-testid={`save-user-${user.id}`}>
                        <Check className="w-4 h-4 mr-2" />Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        <X className="w-4 h-4 mr-2" />Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{user.full_name}</p>
                      <p className="text-sm text-muted-foreground">@{user.username} • {user.role.replace("_", " ")}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(user)}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(user.id)}
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Legacy Clients Modal (import existing clients from ServWare for returning-client lookup)
const LegacyClientsModal = ({ open, onClose, isAdmin }) => {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients`, { params: { search, limit: 200 } });
      setClients(res.data.clients || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (open) fetchClients();
  }, [open, fetchClients]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/clients/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      toast.success(`Imported ${res.data.inserted} client(s)`);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleTemplate = async () => {
    try {
      const res = await axios.get(`${API}/clients/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pfc_legacy_clients_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client record?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Client deleted");
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(`Delete ALL ${total} legacy client records? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/clients`);
      toast.success(`Deleted ${res.data.deleted} client(s)`);
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-white/10">
        <DialogHeader>
          <DialogTitle>Legacy Client List</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel (.xlsx) of existing ServWare / Charity Tracker clients so returning visitors can be recognized at the kiosk.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-clients-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : "Upload CSV"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={handleUpload}
                data-testid="clients-csv-input"
              />
              <Button variant="outline" onClick={handleTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
              {isAdmin && total > 0 && (
                <Button
                  variant="outline"
                  onClick={handleClearAll}
                  className="text-destructive border-destructive/40 hover:bg-destructive/10"
                  data-testid="clear-all-clients-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Supports <strong>.csv</strong> and <strong>.xlsx</strong> files. Required columns: <strong>first_name</strong>, <strong>last_name</strong>, <strong>date_of_birth</strong>. Optional: phone, email, street_address, city, state, zip_code. Dates may be MM/DD/YYYY or YYYY-MM-DD. Charity Tracker exports work directly.
            </p>
            {uploadResult && (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm space-y-1">
                <p className="text-emerald-500 font-medium">Import complete</p>
                <p className="text-muted-foreground">
                  Inserted: <strong>{uploadResult.inserted}</strong> · Duplicates skipped: {uploadResult.skipped_duplicates} · Invalid rows: {uploadResult.skipped_invalid}
                </p>
                {uploadResult.errors?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Show {uploadResult.errors.length} error(s)</summary>
                    <ul className="mt-2 space-y-1 ml-4 list-disc">
                      {uploadResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by first or last name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white/5 border-white/10"
                data-testid="clients-search"
              />
            </div>
            <span className="text-sm text-muted-foreground whitespace-nowrap">{total} total</span>
          </div>

          <div className="rounded-xl border border-white/10 overflow-hidden">
            {loading ? (
              <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : clients.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                No clients yet. Upload a CSV to get started.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">DOB</th>
                    <th className="text-left p-3 font-medium">Phone</th>
                    <th className="text-left p-3 font-medium">Email</th>
                    {isAdmin && <th className="p-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-3">{c.first_name} {c.last_name}</td>
                      <td className="p-3 text-muted-foreground">{c.date_of_birth}</td>
                      <td className="p-3 text-muted-foreground">{c.phone || "—"}</td>
                      <td className="p-3 text-muted-foreground">{c.email || "—"}</td>
                      {isAdmin && (
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(c.id)}
                            className="text-destructive hover:bg-destructive/10"
                            data-testid={`delete-client-${c.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Case Manager Intake Entry
const CaseManagerIntakeEntry = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  // Redirect to main intake but with case_manager mode
  useEffect(() => {
    // For now, redirect to the main intake form
    // Could customize this later for staff-specific features
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>
            <ChevronLeft className="w-5 h-5 mr-2" />
            {t("back")}
          </Button>
          <div>
            <h1 className="text-xl font-bold">{t("newIntake")}</h1>
            <p className="text-sm text-muted-foreground">Entered by {user?.full_name}</p>
          </div>
        </div>
        <PFCLogo className="h-10" />
      </header>
      
      <div className="p-6 text-center">
        <p className="text-muted-foreground mb-4">Staff intake entry uses the same form as client self-service.</p>
        <Button onClick={() => navigate("/intake")}>
          Open Intake Form
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <LanguageProvider>
      <TTSProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<KioskHome />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/intake" element={<IntakeWizard />} />
              <Route path="/returning" element={<ReturningClientLookup />} />
              <Route path="/referral" element={<ConferenceReferralForm />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/intake-entry"
                element={
                  <ProtectedRoute>
                    <CaseManagerIntakeEntry />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </TTSProvider>
    </LanguageProvider>
  );
}

export default App;
